import { QuestionType } from 'consts';
import { Question as QuestionFull } from 'types';
import { indexToLetter } from 'utils';
import ReactPlayer from 'react-player';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
    question: QuestionFull;
    baseClassName: 'answer' | 'question';
    showMultipleChoiceOptions: boolean;
    children?: ReactNode;
}

const MAX_MEDIA_AUTO_RETRIES = 2;

export const Question = (props: Props) => {
    const isMultipleChoice = props.question.answers.length > 1;

    // Remounting ReactPlayer (via key) on every new question/reload guarantees a fresh player instance instead of reusing a possibly stuck one.
    const [mediaReloadToken, setMediaReloadToken] = useState(0);
    const [mediaFailed, setMediaFailed] = useState(false);
    const autoRetriesRef = useRef(0);

    useEffect(() => {
        setMediaReloadToken(0);
        setMediaFailed(false);
        autoRetriesRef.current = 0;
    }, [props.question.contentUrl]);

    const reloadMedia = useCallback(() => {
        autoRetriesRef.current = 0;
        setMediaFailed(false);
        setMediaReloadToken((token) => token + 1);
    }, []);

    const handleMediaReady = useCallback(() => {
        autoRetriesRef.current = 0;
        setMediaFailed(false);
    }, []);

    const handleMediaError = useCallback(() => {
        if (autoRetriesRef.current < MAX_MEDIA_AUTO_RETRIES) {
            autoRetriesRef.current += 1;
            setMediaReloadToken((token) => token + 1);
        } else {
            setMediaFailed(true);
        }
    }, []);

    const renderReloadButton = (corner?: 'top-right') => (
        <Tooltip title={mediaFailed ? 'Media failed to load, click to retry' : 'Reload media'}>
            <IconButton
                size="small"
                onClick={reloadMedia}
                aria-label="reload media"
                style={corner === 'top-right' ? { position: 'absolute', top: 0, right: 0 } : undefined}
            >
                <RefreshIcon color={mediaFailed ? 'error' : 'inherit'} fontSize="small" />
            </IconButton>
        </Tooltip>
    );

    const renderQuestion = (question: Omit<QuestionFull, 'answers'>) => {
        switch (question.questionTypeId) {
            case QuestionType.Image:
                if (!question.contentUrl) {
                    return <div className={`${props.baseClassName} image text${isMultipleChoice ? ' multiple-choice' : ''}`} dangerouslySetInnerHTML={{ __html: question.question! }} />;
                }
                return (
                    <>
                        <div className={`${props.baseClassName} image text${isMultipleChoice ? ' multiple-choice' : ''}`} dangerouslySetInnerHTML={{ __html: question.question! }} />
                        <img className={`${props.baseClassName} image content`} src={question.contentUrl!} />
                    </>
                );
            case QuestionType.Audio:
                if (!question.contentUrl) {
                    return <div className={`${props.baseClassName} audio text${isMultipleChoice ? ' multiple-choice' : ''}`} dangerouslySetInnerHTML={{ __html: question.question! }} />;
                }
                return (
                    <>
                        <div className={`${props.baseClassName} audio text${isMultipleChoice ? ' multiple-choice' : ''}`} dangerouslySetInnerHTML={{ __html: question.question! }} />
                        <div className={`${props.baseClassName} audio content`} style={{ display: 'inline-flex', flexDirection: 'row-reverse', alignItems: 'center', gap: 4 }}>
                            <ReactPlayer
                                key={`${question.contentUrl}-${mediaReloadToken}`}
                                className={`${props.baseClassName} audio player`}
                                url={question.contentUrl!}
                                controls={true}
                                width={400}
                                height={54}
                                playing={true}
                                onReady={handleMediaReady}
                                onError={handleMediaError}
                            />
                            {renderReloadButton()}
                        </div>
                    </>
                );

            case QuestionType.Video:
                if (!question.contentUrl) {
                    return <div className={`${props.baseClassName} video text${isMultipleChoice ? ' multiple-choice' : ''}`} dangerouslySetInnerHTML={{ __html: question.question! }} />;
                }
                return (
                    <>
                        <div
                            className={`${props.baseClassName} video text${isMultipleChoice ? ' multiple-choice' : ''}`}
                            dangerouslySetInnerHTML={{ __html: question.question! }}
                        />
                        <div className={`${props.baseClassName} video content`} style={{ position: 'relative' }}>
                            <ReactPlayer
                                key={`${question.contentUrl}-${mediaReloadToken}`}
                                className={`${props.baseClassName} video player`}
                                url={question.contentUrl!}
                                controls={true}
                                playing={true}
                                onReady={handleMediaReady}
                                onError={handleMediaError}
                            />
                            {renderReloadButton('top-right')}
                        </div>
                    </>
                );

            case QuestionType.Text:
            default:
                return (
                    <div
                        className={`${props.baseClassName} text${isMultipleChoice ? ' multiple-choice' : ''}`}
                        dangerouslySetInnerHTML={{ __html: question.question! }}
                    />
                );
        }
    };

    const renderAnswers = (answers: Pick<QuestionFull, 'answers'>) => {
        return (
            <ul className={`${props.baseClassName} answers`}>
                {answers.answers.map((answer, index) => (
                    <li key={`answer-${index}`}>
                        {indexToLetter(index)}) {answer.answer}
                    </li>
                ))}
            </ul>
        );
    };

    return (
        <div className={props.baseClassName}>
            <>
                {renderQuestion(props.question)}
                {props.showMultipleChoiceOptions && isMultipleChoice && renderAnswers(props.question)}
                {props.children}
            </>
        </div>
    );
};
